import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FIELD_HELP_COLORS } from '~/lib/themes/fieldCueColors';
import { uniqueId } from '~/lib/uniqueId';

/** How long a pointer rests on an empty field before its help shows. */
export const HELP_HOVER_DELAY_MS = 300;
/** How long help waits for a pointer crossing between it and its field. */
export const HELP_LEAVE_DELAY_MS = 100;
/** Space between a field and its help, and kept clear of the window's edges. */
const HELP_GAP = 8;

/** The editor's own hint (as on its toolbar), placed beside the field. */
const HelpBubble = styled.div`
  position: fixed;
  z-index: ${p => p.theme.zIndices.dialogs + 1};
  box-sizing: border-box;
  width: max-content;
  max-width: min(260px, calc(100vw - ${2 * HELP_GAP}px));
  padding: 4px 12px;
  border-radius: 4px;
  background-color: ${FIELD_HELP_COLORS.background};
  color: ${FIELD_HELP_COLORS.text};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  filter: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.16))
    drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05));

  &[hidden] {
    display: none;
  }

  /* A bridge over the gap to the field: a pointer crossing it, however
     slowly, stays on the help. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: ${HELP_GAP}px;
  }

  &[data-side='above']::before {
    top: 100%;
  }

  &[data-side='below']::before {
    bottom: 100%;
  }
`;

type Place = { top: number; left: number; side: 'above' | 'below' };

export interface FieldHelp {
  /** The help's id: the field's description. */
  id?: string;
  /** Hovering the field offers its help. */
  fieldProps: {
    onPointerEnter?: React.PointerEventHandler<HTMLElement>;
    onPointerLeave?: React.PointerEventHandler<HTMLElement>;
  };
  /** Run after the field's own focus and blur handlers. */
  onFocus: (event: React.FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
  /** The help itself, outside the field. */
  bubble: React.ReactNode;
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * Help that says what an empty optional field is for (ENG-13020). It opens
 * when the pointer rests on the empty field or the field gets focus, and
 * stays while either lasts, the pointer may move onto it, until Escape. It
 * describes the field to screen readers, and is never part of the field: it
 * lives outside it, takes no focus and is never saved.
 */
export const useFieldHelp = (
  help: string | undefined,
  empty: boolean
): FieldHelp => {
  // Ids come from one shared counter: only a field with help takes one, the
  // first time it has help, and keeps it while its help comes and goes.
  const ownId = useRef<string>();
  if (help && !ownId.current) ownId.current = uniqueId('field-help');
  const id = ownId.current;
  const field = useRef<HTMLElement | null>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const shown = Boolean(help) && open;

  // Help is offered when hover or focus begins on the empty field; once
  // open, what it says stays true while the author types (WCAG 1.4.13).
  const latest = useRef({ empty, dismissed });
  latest.current = { empty, dismissed };
  const offer = () => {
    if (latest.current.empty && !latest.current.dismissed) setOpen(true);
  };

  const clearTimer = () => clearTimeout(timer.current);
  useEffect(() => clearTimer, []);

  // A field that stops offering help (no longer editable, or no longer a
  // helper tag) forgets its visit, so nothing reopens when it offers it again.
  useEffect(() => {
    if (help) return;
    clearTimer();
    setHovered(false);
    setFocused(false);
    setOpen(false);
    setDismissed(false);
  }, [help]);

  const pointerEnter = (event: React.PointerEvent<HTMLElement>) => {
    // A touch has no hover: a tap focuses the field, which offers its help.
    if (event.pointerType === 'touch') return;
    field.current = event.currentTarget;
    clearTimer();
    if (hovered) return;
    timer.current = setTimeout(() => {
      setHovered(true);
      offer();
    }, HELP_HOVER_DELAY_MS);
  };

  const pointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    clearTimer();
    timer.current = setTimeout(() => setHovered(false), HELP_LEAVE_DELAY_MS);
  };

  const onFocus = (event: React.FocusEvent<HTMLElement>) => {
    field.current = event.currentTarget;
    setFocused(true);
    offer();
  };

  // Once the pointer and focus have both left, it closes, and a dismissed
  // help is offered again on the next visit.
  useEffect(() => {
    if (!hovered && !focused) {
      setOpen(false);
      setDismissed(false);
    }
  }, [hovered, focused]);

  // While help is open, Escape is the help's, wherever focus is: it closes
  // only the help, and the field keeps focus and what the author typed. A
  // dialog around the field never sees it, so it stays open. An Escape that
  // cancels a composition is the composition's: its default is left to the
  // input method, and the help stays.
  useEffect(() => {
    if (!shown) return undefined;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (event.isComposing) return;
      setOpen(false);
      setDismissed(true);
    };
    window.addEventListener('keydown', dismiss, true);
    return () => window.removeEventListener('keydown', dismiss, true);
  }, [shown]);

  // Above the field, so what follows it stays in view, or below it when
  // there is no room above; always inside the window. It follows the field
  // as the author's text grows or wraps it, and the page as it scrolls.
  useLayoutEffect(() => {
    if (!shown) {
      setPlace(null);
      return undefined;
    }
    const placeHelp = () => {
      const anchor = field.current?.getBoundingClientRect();
      const box = bubble.current?.getBoundingClientRect();
      if (!anchor || !box) return;
      const above = anchor.top - HELP_GAP - box.height;
      const top = Math.max(
        HELP_GAP,
        Math.min(
          above >= HELP_GAP ? above : anchor.bottom + HELP_GAP,
          window.innerHeight - HELP_GAP - box.height
        )
      );
      const next: Place = {
        top,
        left: Math.max(
          HELP_GAP,
          Math.min(anchor.left, window.innerWidth - HELP_GAP - box.width)
        ),
        side: top + box.height <= anchor.top ? 'above' : 'below'
      };
      setPlace(current =>
        current?.top === next.top &&
        current.left === next.left &&
        current.side === next.side
          ? current
          : next
      );
    };
    placeHelp();
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(placeHelp)
        : null;
    if (field.current) observer?.observe(field.current);
    if (bubble.current) observer?.observe(bubble.current);
    window.addEventListener('scroll', placeHelp, true);
    window.addEventListener('resize', placeHelp);
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', placeHelp, true);
      window.removeEventListener('resize', placeHelp);
    };
  }, [shown]);

  // A finger pressing the help leaves focus in the field too: React's touch
  // listeners are passive, so the press is held here.
  useEffect(() => {
    const element = bubble.current;
    if (!help || !element) return undefined;
    const hold = (event: TouchEvent) => event.preventDefault();
    element.addEventListener('touchstart', hold, { passive: false });
    return () => element.removeEventListener('touchstart', hold);
  }, [help]);

  if (!help) {
    return {
      fieldProps: {},
      onFocus: () => undefined,
      onBlur: () => undefined,
      bubble: null
    };
  }

  return {
    id,
    fieldProps: { onPointerEnter: pointerEnter, onPointerLeave: pointerLeave },
    onFocus,
    onBlur: () => setFocused(false),
    bubble: createPortal(
      <HelpBubble
        ref={bubble}
        id={id}
        role="tooltip"
        hidden={!shown}
        data-side={place?.side}
        style={
          place
            ? { top: place.top, left: place.left }
            : { top: 0, left: 0, visibility: 'hidden' }
        }
        onPointerEnter={event => {
          if (event.pointerType === 'touch') return;
          clearTimer();
          setHovered(true);
        }}
        onPointerLeave={pointerLeave}
        // Pressing the help leaves focus, and what the author typed, in the
        // field; nothing around the field takes the press for its own.
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseUp={stop}
        onClick={stop}
      >
        {help}
      </HelpBubble>,
      document.body
    )
  };
};
